import React, { useState, useEffect, useCallback } from 'react';
import { eventsApi } from '../../../api/eventsApi';
import PublishModal from '../../../components/publishing/PublishModal';
import {
  ChartBarIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  ShareIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import '../styles/event-statistics.css';
import useEventReport from '../hooks/useEventReport';
import {
  reportOptionLabel,
  reportHeading,
  StatsNote,
  ActionButton,
  StatsMessage,
  StatsStrips,
  NotableSessions,
  FastestSpeakers,
  TopKeywords,
  QaAnalysis,
  ChatbotEmbed,
  GraphEmbed,
} from './EventReportSections';

const SUCCESS_VISIBLE_MS = 5000;

/** A flag that switches itself off SUCCESS_VISIBLE_MS after `flash()`. */
const useFlashFlag = () => {
  const [isOn, setIsOn] = useState(false);
  const flash = useCallback(() => {
    setIsOn(true);
    setTimeout(() => setIsOn(false), SUCCESS_VISIBLE_MS);
  }, []);
  return [isOn, flash, setIsOn];
};

const PublishedLink = ({ url }) => (
  <div className="event-stats-published" style={{ marginTop: 14 }}>
    <div>
      <span>Published on WordPress</span>
      <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
        {url}
      </a>
    </div>
    <a
      className="event-detail-btn"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <ArrowTopRightOnSquareIcon /> Open
    </a>
  </div>
);

/** Event statistics report with the stored-report switch and publish actions. */
const EventReportDisplay = ({ eventId, eventUrl, onPublished }) => {
  const { report, loading, error, catalog, selectedReportId, selectReport, reload } = useEventReport(eventId);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showWordPressPublishModal, setShowWordPressPublishModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, flashPublishSuccess, setPublishSuccess] = useFlashFlag();
  const [publishingFaicon, setPublishingFaicon] = useState(false);
  const [publishSuccessFaicon, flashFaiconSuccess, setPublishSuccessFaicon] = useFlashFlag();
  const [publishError, setPublishError] = useState('');
  const [publishedUrl, setPublishedUrl] = useState(eventUrl || null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [settingOfficial, setSettingOfficial] = useState(false);

  useEffect(() => {
    if (eventUrl) setPublishedUrl(eventUrl);
  }, [eventUrl]);

  const handlePublishToWordPress = () => {
    if (!eventId) return;
    setPublishError('');
    setShowWordPressPublishModal(true);
  };

  const handleConfirmWordPressPublish = async (_selectedLang = 'en', digwatchApiVersion = 'production', sections = null) => {
    if (!eventId) return;
    setPublishing(true);
    setPublishError('');
    setPublishSuccess(false);
    try {
      const result = await eventsApi.publishToWordPress(eventId, digwatchApiVersion, sections, selectedReportId);
      flashPublishSuccess();
      setShowWordPressPublishModal(false);
      if (result.published_url) setPublishedUrl(result.published_url);
      onPublished?.(result.published_url);
    } catch (err) {
      setPublishError(err.message || 'Failed to publish to WordPress');
    } finally {
      setPublishing(false);
    }
  };

  const handlePublishToFaicon = async () => {
    if (!eventId) return;
    setPublishingFaicon(true);
    setPublishError('');
    setPublishSuccessFaicon(false);
    try {
      await eventsApi.publishToFaicon(eventId);
      flashFaiconSuccess();
    } catch (err) {
      setPublishError(err.message || 'Failed to send event report to Faicon');
    } finally {
      setPublishingFaicon(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!eventId) return;
    setDownloadingPdf(true);
    try {
      await eventsApi.downloadReportPdf(eventId);
    } catch (err) {
      console.error('PDF download error:', err);
      setPublishError(err.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleMakeOfficial = async () => {
    if (!eventId || !selectedReportId) return;
    setSettingOfficial(true);
    setPublishError('');
    try {
      await eventsApi.setReportOfficial(eventId, selectedReportId);
      await reload(selectedReportId);
    } catch (err) {
      setPublishError(err.message || 'Failed to move official flag');
    } finally {
      setSettingOfficial(false);
    }
  };

  if (loading) return <StatsNote Icon={ArrowPathIcon} spin>Loading statistics...</StatsNote>;
  if (error) return <StatsNote Icon={ExclamationCircleIcon}>{error}</StatsNote>;
  if (!report) return <StatsNote Icon={ChartBarIcon}>No statistics generated yet for this event.</StatsNote>;

  const storedReports = catalog.reports || [];
  const selectedMeta = storedReports.find((r) => r.id === selectedReportId);

  return (
    <div className="event-stats">
      <div className="event-stats-card">
        <div className="event-stats-head">
          <div className="event-stats-head-main">
            <strong>{report.eventName}</strong>
            <p>
              {reportHeading(report, selectedMeta)}
              {report.reportInfo ? ` · ${report.reportInfo}` : ''}
              {report.totalSessionReports ? ` · ${report.totalSessionReports} sessions included` : ''}
            </p>
            {storedReports.length > 0 && (
              <div className="event-stats-report-switch">
                <label>
                  <span>Stored reports</span>
                  <select
                    value={selectedReportId || ''}
                    onChange={(e) => selectReport(e.target.value)}
                  >
                    {storedReports.map((item) => (
                      <option key={item.id} value={item.id}>{reportOptionLabel(item)}</option>
                    ))}
                  </select>
                </label>
                {selectedMeta && !selectedMeta.is_official && selectedMeta.status === 'COMPLETED' && (
                  <button
                    type="button"
                    className="event-detail-btn"
                    onClick={handleMakeOfficial}
                    disabled={settingOfficial}
                  >
                    {settingOfficial ? 'Updating...' : 'Make official'}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="event-stats-actions">
            <ActionButton
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              busy={downloadingPdf}
              Icon={ArrowDownTrayIcon}
              label="Download PDF"
              busyLabel="Generating..."
            />
            <ActionButton
              onClick={handlePublishToWordPress}
              disabled={publishing}
              busy={publishing}
              done={publishSuccess}
              Icon={GlobeAltIcon}
              label="Publish to WordPress"
              busyLabel="Publishing..."
              doneLabel="Published"
            />
            <ActionButton
              onClick={handlePublishToFaicon}
              disabled={publishingFaicon}
              busy={publishingFaicon}
              done={publishSuccessFaicon}
              Icon={ShareIcon}
              label="Send to Faicon"
              busyLabel="Sending..."
              doneLabel="Sent"
            />
          </div>
        </div>

        {publishError && <StatsMessage kind="error" Icon={ExclamationCircleIcon}>{publishError}</StatsMessage>}
        {publishSuccess && <StatsMessage kind="success" Icon={CheckCircleIcon}>Published to WordPress.</StatsMessage>}
        {publishSuccessFaicon && <StatsMessage kind="success" Icon={CheckCircleIcon}>Sent to Faicon.</StatsMessage>}
        {publishedUrl && <PublishedLink url={publishedUrl} />}
      </div>

      <StatsStrips stats={report.globalStatistics || {}} />
      <NotableSessions notable={report.prominentSessions || {}} />
      {report.fastestSpeakers?.length > 0 && <FastestSpeakers speakers={report.fastestSpeakers} />}
      {report.topPrefixes?.length > 0 && <TopKeywords prefixes={report.topPrefixes} />}
      {report.questions?.length > 0 && (
        <QaAnalysis
          questions={report.questions}
          expandedIndex={expandedQuestion}
          onToggle={(idx) => setExpandedQuestion(expandedQuestion === idx ? null : idx)}
        />
      )}
      {report.chatbot && <ChatbotEmbed src={report.chatbot} />}
      {report.graph_path && <GraphEmbed path={report.graph_path} />}

      <PublishModal
        isOpen={showWordPressPublishModal}
        onClose={() => setShowWordPressPublishModal(false)}
        onConfirm={handleConfirmWordPressPublish}
        destination="wordpress"
        availableDestinations={[]}
        isUpdate={!!publishedUrl}
        isPublishing={publishing}
        availableLanguages={[]}
        showSections
      />
    </div>
  );
};

export default EventReportDisplay;
